import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { ChevronRight, Phone, MessageSquare, ShoppingCart, Calendar, UserCheck, Globe, Languages, Zap, Database, Network, GitBranch, Shield, Award, Users, ArrowRight } from 'lucide-react';
import { mockData } from '../data/mockData';

const Home = () => {
  const [activeAgent, setActiveAgent] = useState(0);
  const [activeDataFeature, setActiveDataFeature] = useState(0);
  const [activeTestimonial, setActiveTestimonial] = useState(0);

  // Auto-rotate testimonials
  useEffect(() => {
    const interval = setInterval(() => {
      setActiveTestimonial((prev) => (prev + 1) % mockData.testimonials.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const agentIcons = [
    { icon: Phone, label: 'AI Voice Agent', color: 'text-blue-600' },
    { icon: MessageSquare, label: 'AI Customer Service Agent', color: 'text-purple-600' },
    { icon: ShoppingCart, label: 'AI Sales Agent', color: 'text-indigo-600' },
    { icon: Calendar, label: 'AI Booking Agent', color: 'text-cyan-600' },
    { icon: UserCheck, label: 'AI Lead Qualification Agent', color: 'text-violet-600' }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 px-6 overflow-hidden bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16 animate-fade-in">
            <h1 className="text-6xl md:text-7xl font-bold mb-6 leading-tight">
              <span className="bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent">
                AI Agents That Deliver
              </span>
              <br />
              <span className="text-gray-900">Human-Like Customer Experiences</span>
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-10">
              Conversational AI agents that understand, interact, and enhance customer experiences—just like human agents would. But infinitely scalable
            </p>
            <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-6 text-lg rounded-full transition-all duration-300 hover:shadow-xl hover:scale-105">
              Get A Demo <ChevronRight className="ml-2" />
            </Button>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-5xl mx-auto">
            {mockData.stats.map((stat, idx) => (
              <div key={idx} className="text-center p-6 bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105">
                <div className="text-4xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent mb-2">
                  {stat.value}
                </div>
                <div className="text-sm text-gray-600">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Trusted Companies */}
      <section className="py-16 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-center mb-12 text-gray-900">
            Trusted by 500+ Leading Enterprises
          </h2>
          <div className="flex flex-wrap justify-center items-center gap-12 opacity-70 hover:opacity-100 transition-opacity duration-300">
            {mockData.trustedCompanies.map((company, idx) => (
              <div key={idx} className="text-2xl font-semibold text-gray-400 hover:text-gray-600 transition-colors duration-300">
                {company}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AI Agents in Action */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-16 text-gray-900">
            Watch Our AI Agents in Action
          </h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            {/* Agent Selector */}
            <div className="space-y-4">
              {mockData.agents.map((agent, idx) => {
                const AgentIcon = agentIcons[idx].icon;
                return (
                  <div
                    key={idx}
                    onClick={() => setActiveAgent(idx)}
                    className={`p-6 rounded-2xl cursor-pointer transition-all duration-300 ${
                      activeAgent === idx
                        ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-2xl scale-105'
                        : 'bg-gray-50 hover:bg-gray-100 text-gray-900'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <AgentIcon className={`w-8 h-8 ${activeAgent === idx ? 'text-white' : agentIcons[idx].color}`} />
                        <div>
                          <h3 className="text-lg font-bold">{agent.name}</h3>
                          <p className={`text-sm ${activeAgent === idx ? 'text-white/80' : 'text-gray-600'}`}>
                            {agent.description}
                          </p>
                        </div>
                      </div>
                      <ChevronRight className={`w-6 h-6 ${activeAgent === idx ? 'text-white' : 'text-gray-400'}`} />
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Agent Preview */}
            <div className="bg-gradient-to-br from-blue-50 to-purple-50 rounded-2xl p-8 flex items-center justify-center">
              <div className="text-center">
                <div className="w-64 h-64 bg-white rounded-2xl shadow-2xl flex items-center justify-center mb-6 animate-pulse">
                  {React.createElement(agentIcons[activeAgent].icon, { className: 'w-32 h-32 text-blue-600' })}
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-2">{mockData.agents[activeAgent].name}</h3>
                <p className="text-gray-600">{mockData.agents[activeAgent].description}</p>
                <Button className="mt-6 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full hover:shadow-xl transition-all duration-300">
                  Explore {mockData.agents[activeAgent].name.split(' ')[1]}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Voice Agents Features */}
      <section className="py-20 px-6 bg-gradient-to-br from-purple-50 via-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-4 text-gray-900">
            Automate Phone Calls with Voice Agents
          </h2>
          <p className="text-xl text-gray-600 text-center mb-16 max-w-4xl mx-auto">
            Expedite resolution even when handling large volumes of phone queries through advanced intent detection, context-aware responses, and multilingual fluency in 100+ languages
          </p>

          <div className="grid md:grid-cols-2 gap-12">
            {mockData.voiceFeatures.map((feature, idx) => (
              <div key={idx} className={`group ${idx % 2 === 0 ? '' : 'md:mt-12'}`}>
                <Card className="overflow-hidden hover:shadow-2xl transition-all duration-500 hover:scale-105 border-0">
                  <CardContent className="p-0">
                    <div className="bg-gradient-to-br from-blue-100 to-purple-100 h-64 flex items-center justify-center">
                      <div className="w-48 h-48 bg-white rounded-full flex items-center justify-center shadow-2xl group-hover:rotate-12 transition-transform duration-500">
                        {React.createElement(feature.icon, { className: 'w-24 h-24 text-blue-600' })}
                      </div>
                    </div>
                    <div className="p-8">
                      <h3 className="text-2xl font-bold mb-3 text-gray-900">{feature.title}</h3>
                      <p className="text-gray-600">{feature.description}</p>
                    </div>
                  </CardContent>
                </Card>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-6 text-lg rounded-full transition-all duration-300 hover:shadow-xl hover:scale-105">
              Experience Voice AI Agents <ChevronRight className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* AI Agent Builder */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-16 text-gray-900">
            Your AI Agent Builder for High-Impact Use Cases
          </h2>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {mockData.builderFeatures.map((feature, idx) => (
              <Card key={idx} className="group hover:shadow-2xl transition-all duration-300 hover:scale-105 border-0 bg-gradient-to-br from-white to-blue-50">
                <CardContent className="p-8 text-center">
                  <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center group-hover:rotate-12 transition-transform duration-500">
                    {React.createElement(feature.icon, { className: 'w-10 h-10 text-white' })}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          <div className="text-center">
            <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-6 text-lg rounded-full transition-all duration-300 hover:shadow-xl hover:scale-105">
              Build Your AI Agent <ChevronRight className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Enterprise Data Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-50 to-purple-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-16 text-gray-900">
            Your Data. Your AI. Your Advantage.
          </h2>

          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div className="space-y-6">
              {mockData.dataFeatures.map((feature, idx) => (
                <div
                  key={idx}
                  onClick={() => setActiveDataFeature(idx)}
                  className={`p-6 rounded-2xl cursor-pointer transition-all duration-300 ${
                    activeDataFeature === idx
                      ? 'bg-white shadow-2xl scale-105'
                      : 'bg-white/50 hover:bg-white/80'
                  }`}
                >
                  <h3 className="text-2xl font-bold mb-2 text-gray-900">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              ))}
            </div>

            <div className="bg-white rounded-2xl shadow-2xl p-8">
              <div className="aspect-video bg-gradient-to-br from-blue-100 to-purple-100 rounded-xl flex items-center justify-center">
                {React.createElement(mockData.dataFeatures[activeDataFeature].icon, { className: 'w-32 h-32 text-blue-600' })}
              </div>
            </div>
          </div>

          <div className="text-center mt-16">
            <Button size="lg" className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-8 py-6 text-lg rounded-full transition-all duration-300 hover:shadow-xl hover:scale-105">
              Get A Demo <ChevronRight className="ml-2" />
            </Button>
          </div>
        </div>
      </section>

      {/* Core Pillars */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-4 text-gray-900">
            Powerful AI That You Control
          </h2>
          <p className="text-xl text-gray-600 text-center mb-16 max-w-4xl mx-auto">
            These core pillars are the foundation of our agentic systems, operating in synergy to drive exponential business value at scale
          </p>

          <div className="grid md:grid-cols-2 gap-8">
            {mockData.corePillars.map((pillar, idx) => (
              <Card key={idx} className="group hover:shadow-2xl transition-all duration-300 hover:scale-105 border-0 overflow-hidden">
                <CardContent className="p-8">
                  <div className="flex items-start gap-6">
                    <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-2xl flex items-center justify-center shrink-0 group-hover:rotate-12 transition-transform duration-500">
                      {React.createElement(pillar.icon, { className: 'w-8 h-8 text-white' })}
                    </div>
                    <div>
                      <h3 className="text-2xl font-bold mb-3 text-gray-900">{pillar.title}</h3>
                      <p className="text-gray-600">{pillar.description}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="py-20 px-6 bg-gradient-to-br from-purple-50 via-blue-50 to-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-4 text-gray-900">
            The Blue Vantage Advantage
          </h2>
          <p className="text-xl text-gray-600 text-center mb-16">
            Purpose-built for efficiency
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {mockData.advantages.map((advantage, idx) => (
              <Card key={idx} className="group hover:shadow-2xl transition-all duration-300 hover:scale-105 border-0 text-center">
                <CardContent className="p-8">
                  <div className="w-20 h-20 mx-auto mb-6 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-500">
                    {React.createElement(advantage.icon, { className: 'w-10 h-10 text-white' })}
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900">{advantage.title}</h3>
                  <p className="text-gray-600">{advantage.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-16 text-gray-900">
            We Couldn't Have Said It Better Ourselves
          </h2>

          <div className="relative">
            <Card className="border-0 shadow-2xl">
              <CardContent className="p-12">
                <div className="text-6xl text-blue-600 mb-6">"</div>
                <p className="text-2xl text-gray-700 mb-8 italic leading-relaxed">
                  {mockData.testimonials[activeTestimonial].quote}
                </p>
                <div className="text-6xl text-blue-600 mb-6 text-right">"</div>
                <div className="flex items-center gap-4">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-600 to-purple-600 rounded-full flex items-center justify-center text-white text-2xl font-bold">
                    {mockData.testimonials[activeTestimonial].name.charAt(0)}
                  </div>
                  <div>
                    <div className="font-bold text-gray-900">{mockData.testimonials[activeTestimonial].name}</div>
                    <div className="text-gray-600">{mockData.testimonials[activeTestimonial].position}</div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Testimonial Indicators */}
            <div className="flex justify-center gap-3 mt-8">
              {mockData.testimonials.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveTestimonial(idx)}
                  className={`w-3 h-3 rounded-full transition-all duration-300 ${
                    activeTestimonial === idx ? 'bg-blue-600 w-8' : 'bg-gray-300 hover:bg-gray-400'
                  }`}
                />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Recognition */}
      <section className="py-20 px-6 bg-gray-50">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-4xl font-bold mb-12 text-gray-900">
            Recognized as a Leading Software Across Categories
          </h2>
          <div className="flex flex-wrap justify-center items-center gap-16 opacity-70">
            {mockData.recognition.map((badge, idx) => (
              <div key={idx} className="text-3xl font-bold text-gray-400">
                {badge}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Section */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-5xl font-bold text-center mb-4 text-gray-900">
            Latest From Our Blog
          </h2>
          <p className="text-xl text-gray-600 text-center mb-16">
            Resources to help your business grow better
          </p>

          <div className="grid md:grid-cols-3 gap-8">
            {mockData.blogPosts.map((post, idx) => (
              <Card key={idx} className="group hover:shadow-2xl transition-all duration-300 hover:scale-105 border-0 cursor-pointer">
                <CardContent className="p-6">
                  <div className="bg-gradient-to-br from-blue-100 to-purple-100 h-48 rounded-xl mb-6 flex items-center justify-center">
                    <div className="text-6xl">📝</div>
                  </div>
                  <h3 className="text-xl font-bold mb-3 text-gray-900 group-hover:text-blue-600 transition-colors">
                    {post.title}
                  </h3>
                  <p className="text-gray-600 mb-4">{post.excerpt}</p>
                  <div className="flex items-center text-blue-600 font-semibold group-hover:gap-3 transition-all">
                    Read now <ArrowRight className="w-4 h-4 ml-2" />
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 bg-gradient-to-br from-blue-600 via-purple-600 to-indigo-600">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-5xl font-bold mb-6 text-white">
            Ready to Shape the Future of Customer Experience?
          </h2>
          <Button size="lg" className="bg-white text-blue-600 hover:bg-gray-100 px-8 py-6 text-lg rounded-full transition-all duration-300 hover:shadow-xl hover:scale-105 font-bold">
            Get A Demo <ChevronRight className="ml-2" />
          </Button>
        </div>
      </section>
    </div>
  );
};

export default Home;